/**
 * Data table for sessions.
 */
import * as React from 'react';
import { Calendar } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { SessionRow } from './SessionRow';
import { SessionCard } from './SessionCard';
import {
  buildRemoteSessionListParams,
  hasRemoteSessionActiveFilters,
  type SessionItemPageActions,
} from './session-table-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { SessionFilters as SessionFiltersContent } from './SessionFilters';
import { SessionDetails } from '@/components/sessions/remote/renderSessionDetails';
import { CancelSessionModal } from './CancelSessionModal';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { useCurrentSchoolYearId } from '@/hooks/useCurrentSchoolYearId';
import { sessionKeys } from '@/hooks/useSessionsRemote';
import {
  getSessions,
  getSession,
  cancelSession,
  exportSessionsExcel,
} from '@/services/remote/sessions.service';
import type { ListItem, SessionTab } from '@/types/remote/session.types';
import { cn } from '@/lib/utils';
import {
  type SessionRemoteFilters,
  DEFAULT_SESSION_REMOTE_FILTERS,
  type SessionRemoteFilterGroupId,
  ALL_SESSION_REMOTE_FILTER_GROUP_IDS,
  SESSION_REMOTE_FILTER_GROUP_LABELS,
  SESSION_REMOTE_TYPE_CONFIG,
} from '@/components/sessions/session-configs';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import type { SessionStatus } from '@/types/inperson/session.types';
import type { ManagedByValue } from '@/components/common/ManagedByMultiSelect';

export type { SessionRemoteFilters };

/** Props for {@link SessionsDataTable}. */
interface SessionsDataTableProps {
  activeTab: SessionTab;
  /** RBAC: tabs the current user is allowed to see (restricts the all-tab query). */
  allowedTabIds?: SessionTab[];
  expandedRowId?: string | null;
  onCloseDetails?: () => void;
  onEditSession?: (sessionId: string) => void;
  onView?: (sessionId: string) => void;
  enableFilters?: boolean;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  pageActions?: SessionItemPageActions;
  initialFilters?: Partial<SessionRemoteFilters>;
  /** Deep-link: keep the list on this tutor (banner lives on the page). */
  pinnedTutorUserId?: string;
  /** Deep-link: keep the list on this student (banner lives on the page). */
  pinnedStudentUserId?: string;
  layoutWidth?: ElementWidth;
  isSection?: boolean;
  showCreateButton?: boolean;
  onCreateSession?: () => void;
  toolbarLeft?: React.ReactNode;
  /** Extra nodes rendered at the start of the section-actions bar (before filter/export/create). */
  toolbarExtra?: React.ReactNode;
  onFiltersChange?: (filters: SessionRemoteFilters) => void;
}

/** Data table for sessions. */
export const SessionsDataTable: React.FC<SessionsDataTableProps> = ({
  activeTab,
  allowedTabIds,
  expandedRowId,
  onCloseDetails,
  onEditSession,
  onView,
  enableFilters = true,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  pageActions,
  initialFilters,
  pinnedTutorUserId,
  pinnedStudentUserId,
  layoutWidth = 'full',
  isSection = false,
  showCreateButton,
  onCreateSession,
  toolbarLeft,
  toolbarExtra,
  onFiltersChange,
}) => {
  const queryClient = useQueryClient();
  const showExport = pageActions?.canExportCsv === true;

  const [cancelId, setCancelId] = React.useState<string | null>(null);

  /** Current school year — used as the default list filter. */
  const currentSchoolYearId = useCurrentSchoolYearId();

  /** Defaults plus current school year, then any parent `initialFilters` override. */
  const defaultFilters = React.useMemo(
    () => ({
      ...DEFAULT_SESSION_REMOTE_FILTERS,
      schoolYearId: currentSchoolYearId,
      ...initialFilters,
    }),
    [initialFilters, currentSchoolYearId],
  );

  const dt = useDataTable<SessionRemoteFilters, SessionRemoteFilterGroupId>({
    tableName: 'sessions',
    defaultFilters,
    allFilterGroupIds: ALL_SESSION_REMOTE_FILTER_GROUP_IDS,
    layoutWidth,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
    onFiltersChange,
  });

  const schoolYearInitRef = React.useRef(false);
  /** One-shot: apply current school year once loaded if not already set. */
  React.useEffect(() => {
    if (schoolYearInitRef.current || !currentSchoolYearId) return;
    schoolYearInitRef.current = true;
    dt.primeFilters((prev) => {
      if (prev.schoolYearId) return prev;
      return { ...prev, schoolYearId: currentSchoolYearId };
    });
  }, [currentSchoolYearId]);

  // Reset to page 1 and close detail row when tab changes — compare with previous value to skip
  // initial mount (and React 18 StrictMode's double-invoke).
  const prevActiveTabRef = React.useRef(activeTab);
  React.useEffect(() => {
    const prev = prevActiveTabRef.current;
    prevActiveTabRef.current = activeTab;
    if (prev === activeTab) return;
    dt.closeExpandedRow();
    dt.setCurrentPage(1);
    dt.setFilters((prev) => ({ ...prev, recurrenceType: undefined }));
  }, [activeTab]);

  /**
   * List query — GET /sessions serves both modes.
   * Remote list must pass `sessionFormat: 'remote'` (types like individual_tutoring overlap with in-person).
   */
  const queryParams = React.useMemo(
    () =>
      buildRemoteSessionListParams(dt.filters, {
        page: dt.currentPage,
        pageSize: dt.itemsPerPage,
        activeTab,
        allowedTabIds,
        pinnedTutorUserId,
        pinnedStudentUserId,
      }),
    [
      activeTab,
      allowedTabIds,
      dt.filters,
      dt.currentPage,
      dt.itemsPerPage,
      pinnedTutorUserId,
      pinnedStudentUserId,
    ],
  );

  const pinnedPersonKey = `${pinnedTutorUserId ?? ''}:${pinnedStudentUserId ?? ''}`;
  const prevPinnedPersonKeyRef = React.useRef(pinnedPersonKey);
  React.useEffect(() => {
    if (prevPinnedPersonKeyRef.current === pinnedPersonKey) return;
    prevPinnedPersonKeyRef.current = pinnedPersonKey;
    dt.setCurrentPage(1);
  }, [pinnedPersonKey, dt.setCurrentPage]);

  /** All-tab waits for RBAC `allowedTabIds` so we never query the full type union. */
  const permissionsReady = activeTab !== 'all' || allowedTabIds !== undefined;
  const { data: sessionsResponse, isLoading } = useQuery({
    queryKey: sessionKeys.list(queryParams),
    queryFn: () => getSessions(queryParams),
    enabled: permissionsReady,
  });

  const sessions = sessionsResponse?.data || [];
  const totalItems = sessionsResponse?.total || 0;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = Math.min(startIndex + dt.itemsPerPage, totalItems);

  const {
    data: expandedSessionData,
    isLoading: isLoadingExpandedSession,
    isError: isExpandedSessionError,
    error: expandedSessionError,
  } = useQuery({
    queryKey: sessionKeys.detail(expandedRowId!),
    queryFn: () => getSession(expandedRowId!),
    enabled: !!expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedSessionError,
    error: expandedSessionError,
    catalogKey: 'sessionNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const expandedItem = (expandedSessionData ?? null) as ListItem | null;

  const [isExporting, setIsExporting] = React.useState(false);
  const isEmptyForExport = !isLoading && totalItems === 0;

  /** Download the current filter set as Excel (same query as the list, no pagination). */
  const handleExportExcel = React.useCallback(async () => {
    if (isEmptyForExport) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      const { page: _page, pageSize: _pageSize, ...exportFilters } = queryParams;
      await exportSessionsExcel(exportFilters);
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [isEmptyForExport, queryParams]);

  const cancelMutation = useMutation({
    mutationFn: ({
      id,
      reasonCode,
      reasonDetail,
    }: {
      id: string;
      reasonCode: string;
      reasonDetail?: string;
    }) => cancelSession(id, { reasonCode: reasonCode as any, reasonDetail }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionKeys.lists() });
      setCancelId(null);
      toast.success('Séance annulée avec succès !');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const sessionToCancel = React.useMemo(
    () => sessions.find((s) => s.id === cancelId),
    [sessions, cancelId],
  );

  /** True when any list filter differs from the table defaults. */
  const hasActiveFilters = React.useMemo(
    () => hasRemoteSessionActiveFilters(dt.filters, defaultFilters),
    [dt.filters, defaultFilters],
  );

  /** Reset filters to the table defaults and jump back to page 1. */
  const clearFilters = React.useCallback(() => {
    dt.setFilters(defaultFilters);
    dt.setCurrentPage(1);
  }, [dt, defaultFilters]);

  const toggleStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        statuses: prev.statuses.includes(status as SessionStatus)
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status as SessionStatus],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setDateRange = React.useCallback(
    (range: SessionRemoteFilters['dateRange']) => {
      dt.setFilters((prev) => ({ ...prev, dateRange: range }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setCustomDateRange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dt.setFilters((prev) => ({
        ...prev,
        customDateFrom: from,
        customDateTo: to,
        dateRange: from || to ? 'all' : prev.dateRange,
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setSearch = React.useCallback(
    (value: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, search: value }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setTutorFilter = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, tutorUserId: id }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setStudentFilter = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, studentUserId: id }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setManagedByFilter = React.useCallback(
    (value: ManagedByValue) => {
      dt.setFilters((prev) => ({ ...prev, managedByIds: value }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setProgramFilter = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, programId: id }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setSchoolYearFilter = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, schoolYearId: id }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setRecurrenceFilter = React.useCallback(
    (v: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, recurrenceType: v }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const customFilterRender = React.useCallback(
    () => (
      <SessionFiltersContent
        activeTab={activeTab}
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onToggleStatus={toggleStatusFilter}
        onSetDateRange={setDateRange}
        onSetCustomDateRange={setCustomDateRange}
        onSetSearch={setSearch}
        onSetTutorFilter={setTutorFilter}
        onSetStudentFilter={setStudentFilter}
        onSetManagedByFilter={setManagedByFilter}
        onSetProgramFilter={setProgramFilter}
        onSetSchoolYearFilter={setSchoolYearFilter}
        onSetRecurrenceFilter={setRecurrenceFilter}
      />
    ),
    [
      activeTab,
      dt.filters,
      dt.allowedFilterIds,
      dt.visibleFilterIds,
      toggleStatusFilter,
      setDateRange,
      setCustomDateRange,
      setSearch,
      setTutorFilter,
      setStudentFilter,
      setManagedByFilter,
      setProgramFilter,
      setSchoolYearFilter,
      setRecurrenceFilter,
    ],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: customFilterRender,
      },
    ],
    [customFilterRender],
  );

  const renderTableHead = () => {
    return (
      <tr>
        <th>Date & horaire</th>
        {activeTab === 'all' && <th>Type</th>}
        {activeTab === 'tutoring' && (
          <>
            <th>Tuteur</th>
            <th>Élève</th>
            <th>Type</th>
          </>
        )}
        {(activeTab === 'information-meeting' ||
          activeTab === 'welcome-meeting' ||
          activeTab === 'field-event') && <th>Animateur(s)</th>}
        {(activeTab === 'information-meeting' || activeTab === 'welcome-meeting') && (
          <th>Participants</th>
        )}
        {activeTab === 'field-event' && (
          <>
            <th>Durée</th>
            <th>Participants</th>
          </>
        )}
        <th>Statut</th>
        <th className="w-28 text-center">Actions</th>
      </tr>
    );
  };

  return (
    <DataTable
      data={sessions}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="séances"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={(() => {
        if (activeTab === 'field-event' || activeTab === 'tutoring') return 6;
        if (activeTab === 'all') return 4;
        return 5;
      })()}
      toolbarActions={
        <>
          {enableFilters && (
            <Button
              preset="toolbar-filter"
              className={cn(dt.showFilters && 'section-action-button-active')}
              showBadge={hasActiveFilters}
              onClick={() => {
                const next = !dt.showFilters;
                dt.setShowFilters(next);
                if (!next) dt.setShowConfigure(false);
              }}
            />
          )}
          {showExport && (
            <Button
              preset="toolbar-export"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isEmptyForExport}
              tooltipText={
                isEmptyForExport && !isExporting
                  ? getErrorMessage('exportEmpty')
                  : 'Exporter en Excel'
              }
            />
          )}
          {showCreateButton && onCreateSession && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer une séance"
              onClick={() => {
                onCloseDetails?.();
                onCreateSession();
              }}
            />
          )}
        </>
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={clearFilters}
            resultCount={totalItems}
            resultLabel="séance(s)"
            onToggleConfigure={
              dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
            }
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ALL_SESSION_REMOTE_FILTER_GROUP_IDS.filter((id) =>
                  dt.allowedFilterIds.includes(id),
                ).map((id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn
                          ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                          : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                      )}
                    >
                      {SESSION_REMOTE_FILTER_GROUP_LABELS[id]}
                    </Button>
                  );
                })}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={renderTableHead}
      renderTableRow={(session) => (
        <SessionRow
          session={session}
          activeTab={activeTab}
          onView={onView}
          onEdit={onEditSession}
          onCancel={(id) => setCancelId(id)}
          pageActions={pageActions}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(session) => (
        <SessionDetails session={session} activeTab={activeTab} pageActions={pageActions} />
      )}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      renderCard={(session) => (
        <SessionCard
          session={session}
          activeTab={activeTab}
          onView={onView}
          onEdit={onEditSession}
          onCancel={(id) => setCancelId(id)}
          pageActions={pageActions}
        />
      )}
      emptyIcon={Calendar}
      emptyText="Aucune séance trouvée"
      title="Séances"
      sidebarMenuId="menu-sessions"
      isSection={isSection}
      toolbarLeft={toolbarLeft}
      toolbarExtra={toolbarExtra}
      expandedItem={expandedItem}
      isExpandedDetailLoading={isLoadingExpandedSession}
      onCloseDetails={onCloseDetails}
      renderDetailsContent={(session) => (
        <SessionDetails session={session} activeTab={activeTab} pageActions={pageActions} />
      )}
      modals={
        <CancelSessionModal
          open={!!cancelId}
          onClose={() => setCancelId(null)}
          onConfirm={async ({ reasonCode, reasonDetail }) => {
            if (cancelId)
              await cancelMutation.mutateAsync({
                id: cancelId,
                reasonCode: reasonCode as any,
                reasonDetail,
              });
          }}
          sessionTitle={
            sessionToCancel
              ? `${SESSION_REMOTE_TYPE_CONFIG[sessionToCancel.type]?.label || sessionToCancel.type} – ${new Date(sessionToCancel.sessionDate).toLocaleDateString('fr-FR')}`
              : undefined
          }
          isSubmitting={cancelMutation.isPending}
        />
      }
    />
  );
};
